import React, { useState } from "react"
import './styleCreateRole.css'
import INCSUBSearchbar from "../common/SearchBar/INCSUBSearchbar";
import INCSUBUserInfoCard from "./UserInfo";
import INCSUBButton from "../common/Button/INCSUBButton";
import { useDispatch, useSelector } from "react-redux";
import { getUserUpdateDetails, searchUserExceptEmployee, updateUserRole } from "../../services/userServices";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { setUserList } from "../../features/users/userSlice";

function INCSUBCreaterole({ handleIsAddEmp }) {

    const dispatch = useDispatch();
    const addedUserList = useSelector((state) => state.user.userList);
    const navigate = useNavigate();
    const [userList, setUserLi] = useState();
    const [selectedUser, setSelectedUser] = useState(null);
    const [isBtnLoader, setIsBtnLoader] = useState(true)



    /**
     * 
     * @author govind dindewad
 * fetchUserExceptEmployee - Fetches a list of users excluding employees.
 *
 * This function interacts with the API to retrieve a filtered list of users who are not employees. 
 * It updates the `userList` state with the fetched data, adding a `type` property to each user 
 * for further operations.
 *
 * @async
 * @function
 * @param {string} [key=""] - Optional search key to filter users by a specific term.
 *
 * @throws Displays an error toast if the API call fails or returns an unexpected status code.
 *
 * Side Effects:
 * - Updates the `userList` state with the fetched users.
 * - Clears user session and redirects to the login page if the API response status is 401.
 *
 * Example Usage:
 * ```javascript
 * fetchUserExceptEmployee("John"); // Fetches users with names matching "John", excluding employees.
 * ```
 */

    const fetchUserExceptEmployee = async (key = '') => {
        try {
            // Make an API call to fetch users excluding employees.
            const userList = await searchUserExceptEmployee(key);

            if (userList.status === 200) {
                // On success, update the `userList` state with users and initialize `type` to 0.
                setUserLi(userList.data.data.map(user => ({ ...user, type: 0 })));
            } else if (userList.status === 401) {
                // If unauthorized, clear local storage and redirect to the login page.
                window.localStorage.setItem('userData', null);
                window.localStorage.setItem('token', null);
                navigate('/login');
            } else if (![200, 401].includes(userList.status)) {
                // For other unexpected statuses, show an error toast.
                toast.error(userList.data.message);
            }
        } catch (error) {
            // Handle any unexpected errors.
            toast.error("Something went wrong"); // Display a toast for user feedback.
        }
    };




    /**
     *  @author govind dindewad
 * INCSUBAddUser - Updates the user list state and sets the selected user with the specified role.
 *
 * This function modifies the `userList` state based on the operation type ('add' or 'remove'). 
 * For 'add', it sets the `type` of the selected user to 1. For 'remove', it resets the user's 
 * `type` to 0. Additionally, it updates the `selectedUser` state with the user object and 
 * role if provided, or resets it to null otherwise.
 *
 * @function
 * @param {Object} obj - The user object being modified.
 * @param {string} operation - The type of operation ('add' or 'remove').
 * @param {string|null} role - The user role to assign. If null, resets the `selectedUser` state.
 *
 * Example Usage:
 * ```javascript
 * INCSUBAddUser(user, "add", "admin"); // Marks the user as added and sets their role to "admin".
 * INCSUBAddUser(user, "remove", null); // Removes the user and resets the selected user state.
 * ```
 */

    const INCSUBAddUser = (obj, operation, role) => {
        if (operation === 'add') {
            // Set the selected user's `type` to 1 and others to 0.
            setUserLi(userList.map(user => ({
                ...user,
                type: user.userId === obj.userId ? 1 : 0
            })));
        } else {
            // Reset the selected user's `type` to 0 while keeping others unchanged.
            setUserLi(userList.map(user => (user.userId === obj.userId ? {
                ...user,
                type: 0
            } : { ...user })));
        }

        if (!role) {
            // If no role is provided, reset the `selectedUser` state and return early.
            setSelectedUser(null);
            return;
        }

        // Update the `selectedUser` state with the user object and assigned role.
        setSelectedUser({ ...obj, userRole: role });
    };




    /**
     *  @author govind dindewad
 * INCSUBAddRoleList - Renders a list of user information cards.
 *
 * This component iterates over the `userList` and renders an `INCSUBUserInfoCard` 
 * for each user. It passes the user object (`obj`) and a callback function 
 * (`functionCrud`) to the card component for CRUD operations.
 *
 * @variable
 * @type {JSX.Element | null}
 *
 * Dependencies:
 * - `INCSUBUserInfoCard`: A card component displaying user information.
 * - `INCSUBAddUser`: Callback function for handling add/remove operations.
 *
 * Example Usage:
 * ```jsx
 * {INCSUBAddRoleList}
 * ```
 */

    const INCSUBAddRoleList =
        userList &&
        userList.length > 0 &&
        userList.map((user) => {
            // Render an INCSUBUserInfoCard for each user in the userList.
            return <INCSUBUserInfoCard obj={user} functionCrud={INCSUBAddUser} />;
        });


    /**
     * @author govind dindewad
 * searchEmp - Fetches users excluding employees based on a search key.
 *
 * This function acts as a wrapper around the `fetchUserExceptEmployee` function, 
 * passing the provided search key to fetch filtered user data.
 *
 * @function
 * @param {string} key - The search term used to filter user data.
 *
 * Example Usage:
 * ```javascript
 * searchEmp("John"); // Fetches users with names matching "John".
 * ```
 */

    const searchEmp = (key) => {
        // Call fetchUserExceptEmployee with the search key to fetch filtered users.
        fetchUserExceptEmployee(key);
    };



    /**
     *  @author govind dindewad
 * handleUpdate - Updates the selected user's role and manages state updates.
 *
 * This function performs the following tasks:
 * 1. Validates if a user is selected before proceeding.
 * 2. Sends an API request to update the selected user's role.
 * 3. On success:
 *    - Removes the updated user from the local `userList` state.
 *    - Adds the updated user to the `adminList` in Redux.
 * 4. Handles specific API error responses:
 *    - Status 401: Logs the user out and redirects to the login page.
 *    - Other statuses: Displays an error toast with the server message.
 *
 * @async
 * @function
 * @throws Displays error messages if the API call or operations fail.
 *
 * Side Effects:
 * - Updates the `userList` state and the Redux store (`adminList`).
 * - Manages button loader and selection states.
 *
 * Example Usage:
 * ```javascript
 * handleUpdate(); // Updates the selected user's role and state.
 * ```
 */

    const handleUpdate = async () => {
        // Validate that a user is selected before proceeding.
        if (!selectedUser) {
            toast.warning("Select User before proceed");
            return;
        }

        setIsBtnLoader(false); // Disable the button loader.

        try {
            // API call to update the user's role.
            const userData = await updateUserRole(selectedUser);

            if (userData.status === 200) {

                // Filter out the selected user from the userList.
                const updatedList = userList && userList.filter((user) => user.userId !== selectedUser.userId);
                setUserLi(updatedList); // Update the local userList state.

                // Get the current admin list from Redux.
                const updatedEmpList = addedUserList.adminList;

                // Update Redux store with the modified admin list.
                dispatch(
                    setUserList({
                        ...userList,
                        adminList: [...updatedEmpList, selectedUser],
                    })
                );
                toast.success("Role Changed Successfully");
                setSelectedUser(null); // Reset the selected user state.
            } else if (userData.status === 401) {
                // Handle unauthorized access by clearing session and redirecting to login.
                window.localStorage.setItem('userData', null);
                window.localStorage.setItem('token', null);
                navigate('/login');
            } else if (![200, 401].includes(userData.status)) {
                // Display an error toast for other unexpected statuses.
                toast.error(userData.data.message);
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            // Re-enable the button loader after the operation.
            setIsBtnLoader(false);
        }
    };





    return (
        <div className="INCSUBCreaterole_ctn">
            <div className="INCSUBCreaterole_card">
                <div className="INCSUBCreaterole_card_head_crossicon">
                    <div className="INCSUBCreaterole_card_head_name_ctn">
                        <div className="INCSUBCreaterole_card_head_name">Add Role</div>
                        <div className="INCSUBCreaterole_card_head_subrole">Super Admin, Admin, Support, Content</div>
                    </div>
                    <div className="INCSUBCreaterole_card_head_crossicon_svg" onClick={() => {
                        handleIsAddEmp(false)
                    }}>
                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M3.68193 3.47683C3.98923 3.16963 4.40595 2.99705 4.84047 2.99705C5.27499 2.99705 5.69171 3.16963 5.99901 3.47683L12.2145 9.69231L18.43 3.47683C18.5811 3.32032 18.762 3.19548 18.9619 3.1096C19.1618 3.02372 19.3768 2.97852 19.5944 2.97662C19.812 2.97473 20.0278 3.01619 20.2292 3.09859C20.4305 3.18098 20.6135 3.30266 20.7674 3.45652C20.9212 3.61038 21.0429 3.79334 21.1253 3.99473C21.2077 4.19611 21.2492 4.41189 21.2473 4.62947C21.2454 4.84706 21.2002 5.06208 21.1143 5.26201C21.0284 5.46193 20.9036 5.64275 20.7471 5.79391L14.5316 12.0094L20.7471 18.2249C21.0456 18.5339 21.2107 18.9479 21.207 19.3775C21.2033 19.8072 21.0309 20.2182 20.7271 20.522C20.4233 20.8258 20.0123 20.9982 19.5826 21.0019C19.153 21.0056 18.739 20.8405 18.43 20.542L12.2145 14.3265L5.99901 20.542C5.68995 20.8405 5.27602 21.0056 4.84637 21.0019C4.41671 20.9982 4.00571 20.8258 3.70189 20.522C3.39807 20.2182 3.22573 19.8072 3.222 19.3775C3.21826 18.9479 3.38343 18.5339 3.68193 18.2249L9.89741 12.0094L3.68193 5.79391C3.37473 5.48661 3.20215 5.06989 3.20215 4.63537C3.20215 4.20085 3.37473 3.78413 3.68193 3.47683Z" fill="black" />
                        </svg>

                    </div>
                </div>
                <div className="INCSUBCreaterole_card_searchbar">
                    <INCSUBSearchbar width={"100%"} backgroundColor={'#F5F6FA'} placeholder={'Search'} param={{ func: searchEmp }} />
                </div>
                <hr style={{ border: '0.5px solid rgba(213, 213, 213, 1)', marginTop: '10px', marginBottom: '10px' }}></hr>
                <div className="INCSUBAddRole_userlist">
                    {INCSUBAddRoleList}
                </div>
                {userList &&
        userList.length > 0 &&<div className="INCSUBCreaterole_card_AddBtn">
                    <INCSUBButton text={'Add'} clickFun={handleUpdate} isLoader={isBtnLoader} />
                </div>}
            </div>
        </div>
    )
}

export default INCSUBCreaterole;





